import { useCallback } from 'react';
import { EAttributeType } from '../../../@types/AttributeTypes';
import { IBuildingProviderContext } from '../../../@types/ContextProviderRelated';
import { EHistoryAction } from '../../../hooks/useUndoRedo';
import { IWall } from '../../../models/abstract/Wall';
import AttributeModel from '../../../models/standard/AttributeModel';
import PassageModel, { IPassage } from '../../../models/standard/PassageModel';
import PartitionWallModel from '../../../models/standard/PartitionWallModel';
import StandardWallModel from '../../../models/standard/StandardWallModel';
import { DefaultAttributeId, getDefaultAttributesForObject, ObjectType } from '../../../utils/attributes/defaultObjectsAndAttributes';

export interface IPassageActions {
  deletePassage: (wallIndex: number, passageIndex: number) => void;
  updatePassageWidth: (wallIndex: number, passageIndex: number, newWidth: number) => void;
  updatePassageAttributes: (wallIndex: number, passageIndex: number, attributes: AttributeModel[]) => void;
  setPassageWidthAttribute: (wallIndex: number, passageIndex: number, width: number, widthUnit: string | null) => void;
}

export const usePassageActions = (setBuilding: React.Dispatch<React.SetStateAction<IBuildingProviderContext>>): IPassageActions => {
  const recreateWallWithPassages = (originalWall: IWall, newPassages: IPassage[]): IWall | null => {
    if (originalWall instanceof StandardWallModel) {
      const radsA = [...originalWall.sides.A.radiators];
      const radsB = [...originalWall.sides.B.radiators];

      const newWall = new StandardWallModel(
        originalWall.startPoint,
        originalWall.endPoint,
        originalWall.depth,
        originalWall.windows,
        originalWall.doors,
        [],
        originalWall.attributes,
        originalWall.uuid,
        newPassages,
      );

      newWall.sides.A.radiators = radsA;
      newWall.sides.B.radiators = radsB;
      newWall.updateWallLengths();
      return newWall;
    }

    if (originalWall instanceof PartitionWallModel) {
      const newWall = new PartitionWallModel(originalWall.startPoint, originalWall.endPoint);
      newWall.depth = originalWall.depth;
      newWall.windows = originalWall.windows;
      newWall.doors = originalWall.doors;
      newWall.passages = newPassages;
      newWall.sides = originalWall.sides;
      newWall.attributes = originalWall.attributes;
      newWall.setUUID(originalWall.uuid);
      newWall.updateWallLengths?.();
      return newWall;
    }

    return null;
  };

  const deletePassage = useCallback(
    (wallIndex: number, passageIndex: number): void => {
      setBuilding((prev) => {
        const walls = [...prev.walls];
        const currentWall = walls[wallIndex];

        if (!currentWall || !currentWall.passages) return prev;

        const passages = [...currentWall.passages];
        passages.splice(passageIndex, 1);

        const newWall = recreateWallWithPassages(currentWall, passages);

        if (newWall) {
          walls[wallIndex] = newWall;
          return { ...prev, walls, isSetByUndoRedo: false, lastAction: EHistoryAction.PASSAGE_DELETED };
        }
        return prev;
      });
    },
    [setBuilding],
  );

  const updatePassageWidth = useCallback(
    (wallIndex: number, passageIndex: number, newWidth: number): void => {
      setBuilding((prev) => {
        const walls = [...prev.walls];
        const currentWall = walls[wallIndex];

        if (!currentWall || !currentWall.passages) return prev;

        const passages = [...currentWall.passages];
        const currentPassage = passages[passageIndex];
        const maxWidth = currentWall.startPoint.distanceTo(currentWall.endPoint);
        const clampedWidth = Math.min(newWidth, maxWidth);
        const updatedPassage = new PassageModel(currentPassage.position, clampedWidth, currentPassage.depth, currentPassage.angle, currentPassage.attributes);
        updatedPassage.setUUID(currentPassage.uuid);
        passages[passageIndex] = updatedPassage;

        const newWall = recreateWallWithPassages(currentWall, passages);

        if (newWall) {
          walls[wallIndex] = newWall;
          return { ...prev, walls, isSetByUndoRedo: false, lastAction: EHistoryAction.PASSAGE_WIDTH_CHANGED };
        }
        return prev;
      });
    },
    [setBuilding],
  );

  const updatePassageAttributes = useCallback(
    (wallIndex: number, passageIndex: number, attributes: AttributeModel[]): void => {
      setBuilding((prev) => {
        const walls = [...prev.walls];
        const currentWall = walls[wallIndex];

        if (!currentWall || !currentWall.passages) return prev;

        const passages = [...currentWall.passages];
        const currentPassage = passages[passageIndex];

        const defaultPassageAttrs = getDefaultAttributesForObject(ObjectType.Passage);
        const updatedAttrs = attributes.map((attr) => {
          const defaultAttr = defaultPassageAttrs.find((def) => def.id === attr.id);
          return new AttributeModel(
            attr.id,
            attr.key,
            attr.value,
            attr.predecessorId,
            attr.type,
            attr.unit,
            attr.hidden ?? defaultAttr?.hidden ?? false,
            attr.forced ?? defaultAttr?.forced ?? false,
            attr.ownerId,
            attr.userId,
            attr.publicFlag,
            attr.localization,
            attr.defaults,
            attr.validation,
            attr.unitFamily,
          );
        });

        const updatedPassage = new PassageModel(currentPassage.position, currentPassage.length, currentPassage.depth, currentPassage.angle, updatedAttrs);
        updatedPassage.setUUID(currentPassage.uuid);
        passages[passageIndex] = updatedPassage;

        const newWall = recreateWallWithPassages(currentWall, passages);

        if (newWall) {
          walls[wallIndex] = newWall;
          return { ...prev, walls, isSetByUndoRedo: false, lastAction: EHistoryAction.PASSAGE_ATTRIBUTES_UPDATED };
        }
        return prev;
      });
    },
    [setBuilding],
  );

  const setPassageWidthAttribute = useCallback(
    (wallIndex: number, passageIndex: number, width: number, widthUnit: string | null): void => {
      setBuilding((prev) => {
        const walls = [...prev.walls];
        const currentWall = walls[wallIndex];

        if (!currentWall || !currentWall.passages) return prev;

        const passages = [...currentWall.passages];
        const currentPassage = passages[passageIndex];

        if (!currentPassage.attributes?.some((attr) => attr.id === DefaultAttributeId.PASSAGE_WIDTH)) {
          return prev;
        }

        const updatedAttrs = currentPassage.attributes.map((attr) => {
          if (attr.id === DefaultAttributeId.PASSAGE_WIDTH) {
            return new AttributeModel(
              attr.id,
              attr.key,
              width,
              attr.predecessorId,
              EAttributeType.NUMBER,
              widthUnit,
              attr.hidden,
              attr.forced,
              attr.ownerId,
              attr.userId,
              attr.publicFlag,
              attr.localization,
              attr.defaults,
              attr.validation,
              attr.unitFamily,
            );
          }
          return attr;
        });

        const updatedPassage = new PassageModel(currentPassage.position, currentPassage.length, currentPassage.depth, currentPassage.angle, updatedAttrs);
        updatedPassage.setUUID(currentPassage.uuid);
        passages[passageIndex] = updatedPassage;

        const newWall = recreateWallWithPassages(currentWall, passages);

        if (newWall) {
          walls[wallIndex] = newWall;
          return { ...prev, walls };
        }
        return prev;
      });
    },
    [setBuilding],
  );

  return {
    deletePassage,
    updatePassageWidth,
    updatePassageAttributes,
    setPassageWidthAttribute,
  };
};
